import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthBaseQuery } from './baseQuery';
import { API_BASE_URL } from '../constants/api';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: createAuthBaseQuery(API_BASE_URL),
  endpoints: (builder) => ({
    requestOtp: builder.mutation({
      query: (body) => ({ url: '/auth/request-otp', method: 'POST', body }),
    }),
    verifyOtp: builder.mutation({
      query: (body) => ({ url: '/auth/verify-otp', method: 'POST', body }),
    }),
    signup: builder.mutation({
      query: (body) => ({ url: '/auth/signup', method: 'POST', body }),
    }),
    // Step 1 of the credentials flow. Body: { identifier, password }.
    // `identifier` is matched against UHID (patients) or Client ID
    // (doctors / lab partners) on the server. On success the server
    // dispatches an OTP to the user's registered phone and returns
    // { requiresOtp, identifier, masked_phone } — NO session yet.
    // Use verifyCredentialsOtp below to complete the login.
    verifyCredentials: builder.mutation({
      query: (body) => ({ url: '/auth/verify-credentials', method: 'POST', body }),
    }),
    // Step 2 of the credentials flow. Body: { identifier, otp }.
    // Verifies the OTP for the same identifier's registered phone
    // and issues the session. Returns { token, user, must_change_password }.
    verifyCredentialsOtp: builder.mutation({
      query: (body) => ({ url: '/auth/verify-credentials-otp', method: 'POST', body }),
    }),
    // ─── Doctor-mode login fork ─────────────────────────────────
    // Step 1. Body: { client_id, phone }. The phone must match a
    // RefDoctor mapped under this centre — otherwise the backend
    // returns 404 with a clear message. Returns { masked_phone,
    // doctor_name } so the UI can show "OTP sent to Dr. X's number".
    sendDoctorOtp: builder.mutation({
      query: (body) => ({ url: '/auth/doctor/send-otp', method: 'POST', body }),
    }),
    // Step 2. Body: { client_id, phone, otp }. Mints a JWT with
    // role='doctor' on success. Returns { token, role, doctor, user }.
    verifyDoctorOtp: builder.mutation({
      query: (body) => ({ url: '/auth/doctor/verify-otp', method: 'POST', body }),
    }),
    // JWT-protected. Body: { new_password }. Returns { ok, user }.
    changePassword: builder.mutation({
      query: (body) => ({ url: '/auth/change-password', method: 'POST', body }),
    }),
    me: builder.query({
      query: () => '/auth/me',
    }),
    updateMe: builder.mutation({
      query: (body) => ({ url: '/auth/me', method: 'PATCH', body }),
    }),
  }),
});

export const {
  useRequestOtpMutation,
  useVerifyOtpMutation,
  useSignupMutation,
  useVerifyCredentialsMutation,
  useVerifyCredentialsOtpMutation,
  useSendDoctorOtpMutation,
  useVerifyDoctorOtpMutation,
  useChangePasswordMutation,
  useMeQuery,
  useUpdateMeMutation,
} = authApi;
